import React, { useState, useEffect, useCallback } from "react";
import { Plus, Activity, Shield, Key } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from "../components/UI";
import Badge from "../components/Badge";
import { fmtDate } from "../utils/format";
import { adminAPI, masterAPI } from "../utils/api";
import { ROLE_LABELS } from "../contexts/AuthContext";

const AdminPage = () => {
  const [tab, setTab]             = useState("users");
  const [users, setUsers]       = useState([]);
  const [audit, setAudit]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]      = useState(false);
  const [search, setSearch]      = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser]  = useState(null);
  const [form, setForm] = useState({ username:"", password:"", name:"", email:"", role:"VERIFIKATOR", unit_id:"" });
  const [refUnit, setRefUnit]    = useState([]);
  const [allPerms, setAllPerms]  = useState([]);
  const [rolePerms, setRolePerms] = useState({});
  const [selectedRole, setSelectedRole] = useState("BENDAHARA");
  const [roleChecked, setRoleChecked] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);
  const [overrideUserId, setOverrideUserId] = useState("");
  const [userOverrides, setUserOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ grants: [], revokes: [] });

  const loadUsers = async (s="") => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit:200, ...(s?{search:s}:{}) });
      const r = await adminAPI.getUsers("?" + qs.toString());
      setUsers(r.data||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadAudit = async (s="") => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit:200, ...(s?{search:s}:{}) });
      const r = await adminAPI.getAuditLog("?" + qs.toString());
      setAudit(r.data||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const [permsRes, rolePermsRes] = await Promise.all([
        adminAPI.getPermissions(),
        adminAPI.getRolePermissions(),
      ]);
      setAllPerms(permsRes.data || []);
      setRolePerms(rolePermsRes.data || {});
      const currentRolePerms = (rolePermsRes.data || {})[selectedRole] || [];
      const checked = {};
      currentRolePerms.forEach(p => { checked[p.id] = true; });
      setRoleChecked(checked);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedRole]);

  const handleSaveRolePerms = async () => {
    setSavingPerms(true);
    try {
      const permIds = Object.entries(roleChecked).filter(([, v]) => v).map(([k]) => k);
      await adminAPI.setRolePermissions(selectedRole, permIds);
      await loadPermissions();
    } catch (e) { alert("Gagal: " + e.message); }
    finally { setSavingPerms(false); }
  };

  const loadUserOverrides = async (userId) => {
    if (!userId) return;
    try {
      const r = await adminAPI.getUserPermissions(userId);
      setUserOverrides(r.data || []);
      const grants = (r.data || []).filter(o => o.granted).map(o => o.permission_id);
      const revokes = (r.data || []).filter(o => !o.granted).map(o => o.permission_id);
      setOverrideForm({ grants, revokes });
    } catch (e) { console.error(e); }
  };

  const handleSaveUserOverrides = async () => {
    if (!overrideUserId) return;
    setSavingPerms(true);
    try {
      await adminAPI.setUserPermissions(overrideUserId, overrideForm);
      await loadUserOverrides(overrideUserId);
    } catch (e) { alert("Gagal: " + e.message); }
    finally { setSavingPerms(false); }
  };

  useEffect(() => {
    setSearch("");
    if (tab==="users") {
      loadUsers();
      masterAPI.getUnit().then(r=>setRefUnit((r.data||[]).map(x=>({value:x.id,label:x.nama})))).catch(()=>{});
    }
    if (tab==="audit") loadAudit();
    if (tab==="permissions") { loadPermissions(); loadUsers(); }
  }, [tab]);

  useEffect(() => {
    if (tab === "permissions") {
      const currentRolePerms = rolePerms[selectedRole] || [];
      const checked = {};
      currentRolePerms.forEach(p => { checked[p.id] = true; });
      setRoleChecked(checked);
    }
  }, [selectedRole, rolePerms, tab]);

  const handleSaveUser = async () => {
    if (!form.username || !form.name || !form.role) return;
    setSaving(true);
    try {
      if (editUser) {
        await adminAPI.updateUser(editUser.id, { name:form.name, email:form.email, role:form.role, unit_id:form.unit_id||null, status:form.status||"AKTIF", ...(form.password?{password:form.password}:{}) });
      } else {
        if (!form.password) return alert("Password wajib diisi untuk pengguna baru");
        await adminAPI.createUser(form);
      }
      setShowModal(false); setEditUser(null);
      setForm({ username:"", password:"", name:"", email:"", role:"VERIFIKATOR", unit_id:"" });
      loadUsers(search);
    } catch(e) { alert("Gagal: " + e.message); }
    finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username:u.username, password:"", name:u.name, email:u.email||"", role:u.role, unit_id:"", status:u.status });
    setShowModal(true);
  };

  return (
    <div>
      <PageHeader title="IT Administration & Governance" subtitle="Manajemen pengguna, hak akses, dan audit trail"/>
      <div style={{ display:"flex", gap:8, marginBottom:20 }}>
        {[{key:"users",label:"Pengguna",icon:<Activity size={14}/>},{key:"permissions",label:"Hak Akses",icon:<Key size={14}/>},{key:"audit",label:"Audit Trail",icon:<Shield size={14}/>}].map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:8,
              border:"1.5px solid", cursor:"pointer", fontSize:13, fontWeight:600, fontFamily:"inherit",
              background:tab===t.key?"#2563EB":"white", color:tab===t.key?"white":"#6B7280",
              borderColor:tab===t.key?"#2563EB":"#E5E7EB" }}>
            {t.icon} {t.label}
          </button>
        ))}
        {tab==="users" && <Btn icon={<Plus size={14}/>} onClick={()=>{ setEditUser(null); setForm({username:"",password:"",name:"",email:"",role:"VERIFIKATOR",unit_id:""}); setShowModal(true); }}>Tambah Pengguna</Btn>}
      </div>
      <Card>
        <div style={{ marginBottom:16 }}>
          <Input value={search} onChange={e=>setSearch(e.target.value)}
            onBlur={()=>tab==="users"?loadUsers(search):loadAudit(search)}
            placeholder={tab==="users"?"Cari username, nama, role…":"Cari user, aksi, resource…"} style={{ maxWidth:360 }}/>
        </div>
        {loading ? <Spinner/> : (
          <>
            {tab==="users" && <Table pageSize={10} cols={[
              { key:"username",   label:"Username" },
              { key:"name",       label:"Nama Lengkap" },
              { key:"role",       label:"Role" },
              { key:"unit",       label:"Unit" },
              { key:"status",     label:"Status", render:v=><Badge status={v}/> },
              { key:"last_login", label:"Last Login", render:v=>v?fmtDate(v):"-" },
              { key:"id", label:"Aksi", render:(_,row)=>(
                <Btn size="sm" variant="secondary" onClick={()=>openEdit(row)}>Edit</Btn>
              )},
            ]} data={users}/>}
            {tab==="audit" && <Table pageSize={10} cols={[
              { key:"id",         label:"#", align:"center" },
              { key:"created_at", label:"Waktu", render:v=>fmtDate(v), nowrap:true },
              { key:"username",   label:"User" },
              { key:"role",       label:"Role" },
              { key:"action",     label:"Aksi", render:v=><code style={{background:"#F1F5F9",padding:"2px 6px",borderRadius:4,fontSize:11}}>{v}</code> },
              { key:"resource",   label:"Resource" },
              { key:"ip_address", label:"IP Address", nowrap:true },
            ]} data={audit}/>}
            {tab==="permissions" && (
              <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
                <div style={{ flex:"1 1 500px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"#374151" }}>Role:</span>
                    <select value={selectedRole} onChange={e=>setSelectedRole(e.target.value)}
                      style={{ fontSize:12, padding:"6px 10px", borderRadius:6, border:"1px solid #D1D5DB", fontWeight:600 }}>
                      {["BENDAHARA","VERIFIKATOR","PEJABAT","EKSEKUTIF","FINANCE","PERSONEL"].map(r=>(
                        <option key={r} value={r}>{r} — {ROLE_LABELS[r]||r}</option>
                      ))}
                    </select>
                    <Btn size="sm" onClick={handleSaveRolePerms} disabled={savingPerms}>{savingPerms?"Menyimpan…":"Simpan Hak Akses Role"}</Btn>
                  </div>
                  {(() => {
                    const modules = {};
                    allPerms.forEach(p => { if (!modules[p.module]) modules[p.module] = []; modules[p.module].push(p); });
                    return Object.entries(modules).map(([mod, perms]) => (
                      <div key={mod} style={{ marginBottom:12 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#2563EB", textTransform:"uppercase", marginBottom:4 }}>{mod}</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {perms.map(p => (
                            <label key={p.id} style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:"#374151", cursor:"pointer",
                              background: roleChecked[p.id] ? "#DBEAFE" : "#F9FAFB", padding:"4px 8px", borderRadius:6, border:"1px solid", borderColor: roleChecked[p.id] ? "#93C5FD" : "#E5E7EB" }}>
                              <input type="checkbox" checked={!!roleChecked[p.id]} onChange={e => setRoleChecked(prev=>({...prev,[p.id]:e.target.checked}))} />
                              <span style={{ fontWeight:600 }}>{p.action}</span>
                              <span style={{ color:"#9CA3AF", fontSize:11 }}>({p.label})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                <div style={{ flex:"1 1 340px", borderLeft:"1px solid #E5E7EB", paddingLeft:24 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827", marginBottom:12 }}>Override Per-User</div>
                  <div style={{ display:"flex", gap:8, marginBottom:12, alignItems:"center" }}>
                    <select value={overrideUserId} onChange={e=>{setOverrideUserId(e.target.value); loadUserOverrides(e.target.value);}}
                      style={{ fontSize:12, padding:"6px 10px", borderRadius:6, border:"1px solid #D1D5DB", flex:1 }}>
                      <option value="">Pilih pengguna…</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                  {overrideUserId && (
                    <>
                      <div style={{ fontSize:12, color:"#6B7280", marginBottom:8 }}>Grant (tambah izin):</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:12 }}>
                        {allPerms.map(p => (
                          <label key={`g-${p.id}`} style={{ display:"flex", alignItems:"center", gap:3, fontSize:11, cursor:"pointer",
                            background: overrideForm.grants.includes(p.id) ? "#D1FAE5" : "#F9FAFB", padding:"3px 6px", borderRadius:4, border:"1px solid",
                            borderColor: overrideForm.grants.includes(p.id) ? "#6EE7B7" : "#E5E7EB" }}>
                            <input type="checkbox" checked={overrideForm.grants.includes(p.id)}
                              onChange={e => setOverrideForm(prev => ({
                                ...prev,
                                grants: e.target.checked ? [...prev.grants, p.id] : prev.grants.filter(x=>x!==p.id),
                                revokes: prev.revokes.filter(x=>x!==p.id),
                              }))} />
                            {p.id}
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize:12, color:"#6B7280", marginBottom:8 }}>Revoke (cabut izin):</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:12 }}>
                        {allPerms.map(p => (
                          <label key={`r-${p.id}`} style={{ display:"flex", alignItems:"center", gap:3, fontSize:11, cursor:"pointer",
                            background: overrideForm.revokes.includes(p.id) ? "#FEE2E2" : "#F9FAFB", padding:"3px 6px", borderRadius:4, border:"1px solid",
                            borderColor: overrideForm.revokes.includes(p.id) ? "#FCA5A5" : "#E5E7EB" }}>
                            <input type="checkbox" checked={overrideForm.revokes.includes(p.id)}
                              onChange={e => setOverrideForm(prev => ({
                                ...prev,
                                revokes: e.target.checked ? [...prev.revokes, p.id] : prev.revokes.filter(x=>x!==p.id),
                                grants: prev.grants.filter(x=>x!==p.id),
                              }))} />
                            {p.id}
                          </label>
                        ))}
                      </div>
                      <Btn size="sm" onClick={handleSaveUserOverrides} disabled={savingPerms}>{savingPerms?"Menyimpan…":"Simpan Override"}</Btn>
                      {userOverrides.length > 0 && (
                        <div style={{ marginTop:12 }}>
                          <div style={{ fontSize:12, fontWeight:600, marginBottom:4 }}>Override aktif:</div>
                          {userOverrides.map(o => (
                            <div key={o.permission_id} style={{ fontSize:11, padding:"3px 0", display:"flex", gap:6 }}>
                              <code style={{ background:"#F1F5F9", padding:"1px 4px", borderRadius:3 }}>{o.permission_id}</code>
                              <span style={{ color: o.granted ? "#059669" : "#DC2626", fontWeight:600 }}>{o.granted ? "GRANTED" : "REVOKED"}</span>
                              <span style={{ color:"#9CA3AF" }}>oleh {o.granted_by_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {showModal && (
        <Modal title={editUser?"Edit Pengguna":"Tambah Pengguna Baru"} onClose={()=>{ setShowModal(false); setEditUser(null); }}>
          {!editUser && <Field label="Username" required><Input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="username_baru"/></Field>}
          <Field label="Nama Lengkap" required><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Nama Lengkap"/></Field>
          <Field label="Email"><Input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} type="email" placeholder="email@ifms.go.id"/></Field>
          <Field label="Role" required>
            <Select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}
              options={["ADMIN","BENDAHARA","PEJABAT","VERIFIKATOR","EKSEKUTIF","FINANCE","PERSONEL"].map(v=>({value:v,label:`${v} — ${ROLE_LABELS[v]||v}`}))}/>
          </Field>
          <Field label="Unit Kerja">
            <Select value={form.unit_id} onChange={e=>setForm({...form,unit_id:e.target.value})} options={refUnit} placeholder="Pilih unit…"/>
          </Field>
          {editUser && (
            <Field label="Status">
              <Select value={form.status||"AKTIF"} onChange={e=>setForm({...form,status:e.target.value})}
                options={["AKTIF","NONAKTIF"].map(v=>({value:v,label:v}))}/>
            </Field>
          )}
          <Field label={editUser?"Password Baru (kosongkan jika tidak diganti)":"Password"} required={!editUser}>
            <Input value={form.password} onChange={e=>setForm({...form,password:e.target.value})} type="password" placeholder="Minimal 8 karakter"/>
          </Field>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="secondary" onClick={()=>{ setShowModal(false); setEditUser(null); }}>Batal</Btn>
            <Btn onClick={handleSaveUser} disabled={saving}>{saving?"Menyimpan…":"Simpan"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPage;
